import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../../../../config';

// รายการกระสอบของรอบ — every บรรจุ / เพิ่ม / กระสอบกลับ / เช็คก่อนเพิ่ม row of the round, one per line,
// so a double-typed count can be seen and fixed here instead of by SQL (Vector 2026-10-28).
// Writes go through the existing endpoints; the server keeps the rules (verified round, confirmed rows).
const KIND_LABEL = { LOAD_OUT: 'บรรจุ', REFILL: 'เพิ่ม', RETURN: 'กระสอบกลับจบกะ', BEFORE_CHECK: 'เช็คก่อนเพิ่ม' };
const KIND_ORDER = ['LOAD_OUT', 'REFILL', 'RETURN', 'BEFORE_CHECK'];
// Same rule as BagLedgerAccounting.js hasTemplateEditPermission — every write endpoint here is report:write.
const canWriteBags = () => {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    const perms = user?.permissions || [];
    return perms.includes('superadmin:*') || perms.includes('report:write');
};
const authJson = () => {
    const token = JSON.parse(localStorage.getItem('user') || 'null')?.token;
    return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
};
const formatHHmm = (v) => {
    if (!v) return '';
    const t = String(v).includes('T') ? String(v).split('T')[1] : String(v);
    return t.slice(0, 5);
};
const numOrZero = (v) => (v === '' || v == null ? 0 : Number(v));
const inputStyle = { width: 80, padding: '4px 6px', borderRadius: 4, border: '1px solid #ccc' };
const cellNum = { textAlign: 'right', whiteSpace: 'nowrap' };

// ลงกระสอบเก็บระหว่างทางซ้ำกับกระสอบกลับจบกะ (BagLedgerSummaryService นับทั้งสองทาง) — 10-26 ยอดซ้ำจนเกิน
const looksLikeReturnDouble = (collectedTube, collectedCrushed, ret) => {
    if (!ret) return false;
    const near = (a, b) => a > 0 && Math.abs(a - b) <= 1;
    return near(numOrZero(collectedTube), numOrZero(ret.tubeQty)) || near(numOrZero(collectedCrushed), numOrZero(ret.crushedQty));
};

const BagEntriesPanel = ({ reportCode, onChanged }) => {
    const [data, setData] = useState(null);
    const [loadError, setLoadError] = useState('');
    const [message, setMessage] = useState('');
    const [editingKey, setEditingKey] = useState(null);
    const [draft, setDraft] = useState({});
    const [adding, setAdding] = useState(false);
    const [newEntry, setNewEntry] = useState({ kind: 'LOAD_OUT', tube: '', crushed: '', collectedTube: '', collectedCrushed: '', note: '' });
    const [busy, setBusy] = useState(false);
    const canWrite = canWriteBags();

    const load = async () => {
        if (!reportCode) return;
        try {
            const res = await fetch(`${API_BASE_URL}/saledata/api/module/bag-entries/for-report/${encodeURIComponent(reportCode)}`, {
                headers: { Accept: 'application/json', Authorization: `Bearer ${JSON.parse(localStorage.getItem('user') || 'null')?.token}` },
            });
            if (!res.ok) throw new Error(`bag-entries ${res.status}`);
            setData(await res.json());
            setLoadError('');
        } catch (e) {
            setLoadError('โหลดรายการกระสอบไม่สำเร็จ: ' + e.message);
        }
    };

    useEffect(() => {
        setData(null);
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportCode]);

    const entries = data?.entries || [];
    // "ครั้งที่" counts per kind in time order — the row's own number, not a database id
    const ordinal = new Map();
    const numbered = entries.map((e) => {
        const n = (ordinal.get(e.kind) || 0) + 1;
        ordinal.set(e.kind, n);
        return { ...e, ordinal: n };
    });
    const returnRow = entries.find((e) => e.kind === 'RETURN');
    const countOf = (kind) => entries.filter((e) => e.kind === kind).length;
    const totalCount = KIND_ORDER.filter((k) => k !== 'BEFORE_CHECK').reduce((s, k) => s + countOf(k), 0);

    const afterChange = async (text) => {
        setMessage(text);
        setEditingKey(null);
        await load();
        if (onChanged) await onChanged();
    };

    // 403 = บัญชีไม่มี report:write — ข้อความจากเซิร์ฟเวอร์เป็นอังกฤษดิบ ๆ จึงแปลงเป็นภาษาคน
    const failText = async (res) => {
        if (res?.status === 403) return 'ไม่มีสิทธิ์ทำรายการนี้ (ต้องมีสิทธิ์ report:write)';
        return (await res?.text().catch(() => '')) || String(res?.status ?? '');
    };

    const startEdit = (row) => {
        setEditingKey(`${row.kind}:${row.id}`);
        setDraft({
            tube: row.tubeQty ?? '',
            crushed: row.crushedQty ?? '',
            collectedTube: row.collectedTubeQty ?? '',
            collectedCrushed: row.collectedCrushedQty ?? '',
            note: row.note ?? '',
        });
        setMessage('');
    };

    const saveEdit = async (row) => {
        if (row.kind === 'REFILL' && looksLikeReturnDouble(draft.collectedTube, draft.collectedCrushed, returnRow)) {
            if (!window.confirm(`เก็บระหว่างทาง ${numOrZero(draft.collectedTube)}/${numOrZero(draft.collectedCrushed)} ใกล้เคียงกับกระสอบกลับจบกะ (${returnRow.tubeQty}/${returnRow.crushedQty}) — อาจลงซ้ำ ยืนยันบันทึกหรือไม่?`)) return;
        }
        setBusy(true);
        setMessage('');
        try {
            let res;
            const base = { businessDate: data.businessDate, route: data.route, sellerUid: data.sellerUid };
            if (row.kind === 'LOAD_OUT') {
                res = await fetch(`${API_BASE_URL}/saledata/api/module/bag-load-out/${row.id}`, {
                    method: 'PUT', headers: authJson(),
                    body: JSON.stringify({ ...base, driverName: row.driverName, vehiclePlate: row.vehiclePlate, note: draft.note, tubeQty: numOrZero(draft.tube), crushedQty: numOrZero(draft.crushed) }),
                });
            } else if (row.kind === 'REFILL') {
                res = await fetch(`${API_BASE_URL}/saledata/api/module/bag-refill/${row.id}`, {
                    method: 'PUT', headers: authJson(),
                    body: JSON.stringify({ ...base, vehiclePlate: row.vehiclePlate, note: draft.note, tubeQty: numOrZero(draft.tube), crushedQty: numOrZero(draft.crushed), collectedTubeQty: numOrZero(draft.collectedTube), collectedCrushedQty: numOrZero(draft.collectedCrushed) }),
                });
            } else if (row.kind === 'RETURN') {
                res = await fetch(`${API_BASE_URL}/saledata/api/module/bag-return/for-report/${encodeURIComponent(reportCode)}`, {
                    method: 'PUT', headers: authJson(),
                    body: JSON.stringify({ returnedTubeQty: numOrZero(draft.tube), returnedCrushedQty: numOrZero(draft.crushed), note: draft.note }),
                });
            }
            if (!res || !res.ok) {
                setMessage('แก้ไขไม่สำเร็จ: ' + (await failText(res)));
                return;
            }
            await afterChange('แก้ไขแล้ว');
        } catch (e) {
            setMessage('แก้ไขไม่สำเร็จ: ' + e.message);
        } finally {
            setBusy(false);
        }
    };

    const removeRow = async (row) => {
        // บรรจุที่เครื่องบรรจุยืนยันแล้ว: ลบได้ (ผ่าน shift-admin มี log) แต่ต้องเตือนให้เห็นชัดก่อน
        if (row.confirmedAt && !window.confirm(`แถวนี้เครื่องบรรจุยืนยันแล้ว (หลอด ${row.confirmedTubeQty ?? 0} บด ${row.confirmedCrushedQty ?? 0}) — ลบแล้วยอดบรรจุของรอบจะเปลี่ยน ยืนยันจะลบต่อหรือไม่?`)) return;
        const reason = window.prompt(`ลบ${KIND_LABEL[row.kind]} ครั้งที่ ${row.ordinal} — ใส่เหตุผลที่ลบ (ยกเลิก = ไม่ลบ)`);
        if (!reason || !reason.trim()) return;
        setBusy(true);
        setMessage('');
        try {
            const res = await fetch(`${API_BASE_URL}/saledata/api/truck-shifts/admin/delete`, {
                method: 'POST', headers: authJson(),
                body: JSON.stringify({ kind: row.kind, id: row.id, reason: reason.trim() }),
            });
            if (!res.ok) {
                setMessage('ลบไม่สำเร็จ: ' + (await failText(res)));
                return;
            }
            await afterChange('ลบแล้ว (เก็บสำเนาไว้ในประวัติการลบ)');
        } catch (e) {
            setMessage('ลบไม่สำเร็จ: ' + e.message);
        } finally {
            setBusy(false);
        }
    };

    const addEntry = async () => {
        const isReturn = newEntry.kind === 'RETURN';
        if (!isReturn && looksLikeReturnDouble(newEntry.collectedTube, newEntry.collectedCrushed, returnRow)) {
            if (!window.confirm(`เก็บระหว่างทาง ${numOrZero(newEntry.collectedTube)}/${numOrZero(newEntry.collectedCrushed)} ใกล้เคียงกับกระสอบกลับจบกะ (${returnRow.tubeQty}/${returnRow.crushedQty}) — อาจลงซ้ำ ยืนยันบันทึกหรือไม่?`)) return;
        }
        setBusy(true);
        setMessage('');
        try {
            const base = { businessDate: data.businessDate, route: data.route, sellerUid: data.sellerUid };
            // กะของรอบนี้ — ส่งทุกครั้ง ไม่งั้นเซิร์ฟเวอร์จะเดากะจากเวลาปัจจุบัน (ลงกะผิดถ้ารอบย้อนหลัง)
            const roundShift = { shiftId: data.shiftId };
            let res;
            if (newEntry.kind === 'LOAD_OUT') {
                res = await fetch(`${API_BASE_URL}/saledata/api/module/bag-load-out`, {
                    method: 'POST', headers: authJson(),
                    body: JSON.stringify({ ...base, ...roundShift, note: newEntry.note, tubeQty: numOrZero(newEntry.tube), crushedQty: numOrZero(newEntry.crushed) }),
                });
            } else if (newEntry.kind === 'REFILL') {
                res = await fetch(`${API_BASE_URL}/saledata/api/module/bag-refill`, {
                    method: 'POST', headers: authJson(),
                    body: JSON.stringify({ ...base, ...roundShift, note: newEntry.note, tubeQty: numOrZero(newEntry.tube), crushedQty: numOrZero(newEntry.crushed), collectedTubeQty: numOrZero(newEntry.collectedTube), collectedCrushedQty: numOrZero(newEntry.collectedCrushed) }),
                });
            } else {
                res = await fetch(`${API_BASE_URL}/saledata/api/module/bag-return/for-report/${encodeURIComponent(reportCode)}`, {
                    method: 'PUT', headers: authJson(),
                    body: JSON.stringify({ returnedTubeQty: numOrZero(newEntry.tube), returnedCrushedQty: numOrZero(newEntry.crushed), note: newEntry.note }),
                });
            }
            if (!res.ok) {
                setMessage('เพิ่มไม่สำเร็จ: ' + (await failText(res)));
                return;
            }
            setNewEntry({ kind: newEntry.kind, tube: '', crushed: '', collectedTube: '', collectedCrushed: '', note: '' });
            setAdding(false);
            await afterChange(`บันทึกรายการ '${KIND_LABEL[newEntry.kind]}' แล้ว`);
        } catch (e) {
            setMessage('เพิ่มไม่สำเร็จ: ' + e.message);
        } finally {
            setBusy(false);
        }
    };

    if (loadError) return <div style={{ color: '#c62828', margin: '8px 0' }}>{loadError}</div>;
    if (!data) return <div style={{ color: '#888', margin: '8px 0' }}>กำลังโหลดรายการกระสอบ…</div>;

    const cellInput = (key, width) => (
        <input type="number" min="0" value={draft[key]} onChange={(e) => setDraft({ ...draft, [key]: e.target.value })} style={{ ...inputStyle, width: width || 64 }} />
    );

    return (
        <div data-testid="bag-entries-panel" style={{ margin: '8px 0 16px', fontSize: 14 }}>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'baseline', marginBottom: 6 }}>
                <strong>กรอกแล้ว {totalCount} ครั้ง</strong>
                {KIND_ORDER.map((k) => (
                    <span key={k} style={{ color: '#495057', whiteSpace: 'nowrap' }}>{KIND_LABEL[k]} {countOf(k)} ครั้ง</span>
                ))}
            </div>

            {numbered.length === 0 ? (
                <div style={{ color: '#888' }}>ยังไม่มีรายการกระสอบในรอบนี้</div>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    {/* แท็บเล็ตแนวตั้ง 800px: ไม่ตั้งความกว้างขั้นต่ำ — เวลา/ชนิดรวมเข้าคอลัมน์ลำดับ เพื่อไม่ต้องเลื่อนแนวนอน */}
                    <table className="btc-table">
                        <thead>
                            <tr>
                                <th>ลำดับ</th><th>หลอด</th><th>บด</th><th>เก็บระหว่างทาง</th><th>หมายเหตุ</th><th>จัดการ</th>
                            </tr>
                        </thead>
                        <tbody>
                            {numbered.map((row) => {
                                const key = `${row.kind}:${row.id}`;
                                const isEditing = editingKey === key;
                                const label = `${KIND_LABEL[row.kind]} ครั้งที่ ${row.ordinal}`;
                                return (
                                    <tr key={key}>
                                        <td>
                                            <div style={{ whiteSpace: 'nowrap' }}>{label}</div>
                                            <div style={{ fontSize: 12, color: '#6c757d', whiteSpace: 'nowrap' }}>{formatHHmm(row.createdAt)}</div>
                                            {row.kind === 'BEFORE_CHECK' && <div style={{ fontSize: 12, color: '#6c757d' }}>เหลือในถุง (ดูอย่างเดียว)</div>}
                                            {row.kind === 'LOAD_OUT' && row.confirmedAt && (
                                                <div style={{ fontSize: 12, color: '#ef6c00' }}>
                                                    แถวนี้เครื่องบรรจุยืนยันแล้ว {formatHHmm(row.confirmedAt)} · หลอด {row.confirmedTubeQty ?? 0} บด {row.confirmedCrushedQty ?? 0} — แก้ตัวเลขไม่ได้ ลบได้
                                                </div>
                                            )}
                                        </td>
                                        <td style={cellNum}>{isEditing ? cellInput('tube') : (row.tubeQty ?? 0)}</td>
                                        <td style={cellNum}>{isEditing ? cellInput('crushed') : (row.crushedQty ?? 0)}</td>
                                        <td style={cellNum}>
                                            {row.kind === 'REFILL'
                                                ? (isEditing ? <span style={{ whiteSpace: 'nowrap' }}>{cellInput('collectedTube', 56)} / {cellInput('collectedCrushed', 56)}</span> : `${row.collectedTubeQty ?? 0} / ${row.collectedCrushedQty ?? 0}`)
                                                : '-'}
                                        </td>
                                        <td>{isEditing ? <input type="text" value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} style={{ ...inputStyle, width: 110 }} /> : (row.note || '')}</td>
                                        <td style={{ whiteSpace: 'nowrap' }}>
                                            {isEditing ? (
                                                <>
                                                    <button className="btc-btn btn-sm" disabled={busy} onClick={() => saveEdit(row)}>บันทึก</button>{' '}
                                                    <button className="btc-btn btn-sm" disabled={busy} onClick={() => setEditingKey(null)}>ยกเลิก</button>
                                                </>
                                            ) : (
                                                <>
                                                    {canWrite && row.editable && <button className="btc-btn btn-sm" disabled={busy || !!editingKey} onClick={() => startEdit(row)}>แก้ไข</button>}{' '}
                                                    {canWrite && row.deletable && <button className="btc-btn btn-sm" style={{ backgroundColor: '#c62828' }} disabled={busy || !!editingKey} onClick={() => removeRow(row)}>ลบ</button>}
                                                    {canWrite && row.kind === 'RETURN' && !row.deletable && <div style={{ fontSize: 12, color: '#6c757d' }}>ลบไม่ได้ — แก้ตัวเลขแทน</div>}
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {canWrite && !adding && (
                <button className="btc-btn btn-sm" style={{ marginTop: 8 }} disabled={busy || !!editingKey || !data.shiftId} onClick={() => setAdding(true)}>+ เพิ่มรายการ</button>
            )}
            {canWrite && !adding && !data.shiftId && (
                <div style={{ marginTop: 6, fontSize: 12, color: '#c62828' }}>รอบนี้ยังไม่มีกะ — เพิ่มรายการย้อนหลังลงกะของรอบไม่ได้ (ต้องให้กะของรอบก่อน)</div>
            )}
            {canWrite && adding && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
                    <select value={newEntry.kind} onChange={(e) => setNewEntry({ ...newEntry, kind: e.target.value })} style={{ padding: '4px 6px' }}>
                        <option value="LOAD_OUT">บรรจุ</option>
                        <option value="REFILL">เพิ่ม</option>
                        <option value="RETURN" disabled={!!returnRow}>กระสอบกลับจบกะ{returnRow ? ' (มีแล้ว — แก้แถวเดิม)' : ''}</option>
                    </select>
                    <span>หลอด</span>
                    <input type="number" min="0" value={newEntry.tube} onChange={(e) => setNewEntry({ ...newEntry, tube: e.target.value })} style={inputStyle} />
                    <span>บด</span>
                    <input type="number" min="0" value={newEntry.crushed} onChange={(e) => setNewEntry({ ...newEntry, crushed: e.target.value })} style={inputStyle} />
                    {newEntry.kind === 'REFILL' && (
                        <>
                            <span>เก็บระหว่างทาง</span>
                            <input type="number" min="0" value={newEntry.collectedTube} onChange={(e) => setNewEntry({ ...newEntry, collectedTube: e.target.value })} style={{ ...inputStyle, width: 64 }} />
                            <span>/</span>
                            <input type="number" min="0" value={newEntry.collectedCrushed} onChange={(e) => setNewEntry({ ...newEntry, collectedCrushed: e.target.value })} style={{ ...inputStyle, width: 64 }} />
                        </>
                    )}
                    <input type="text" placeholder="หมายเหตุ" value={newEntry.note} onChange={(e) => setNewEntry({ ...newEntry, note: e.target.value })} style={{ ...inputStyle, width: 140 }} />
                    <button className="btc-btn btn-sm" disabled={busy} onClick={addEntry}>บันทึกรายการ</button>
                    <button className="btc-btn btn-sm" disabled={busy} onClick={() => setAdding(false)}>ยกเลิก</button>
                </div>
            )}

            {message && <div style={{ marginTop: 8, color: message.includes('ไม่สำเร็จ') ? '#c62828' : '#2e7d32' }}>{message}</div>}
        </div>
    );
};

export default BagEntriesPanel;
